/**
 * Pure flow/screen state machine for the Client Node onboarding shell.
 *
 * No DOM, no fetch, no timers, no globals — the gate (Task 8) owns wiring this to the
 * `onboarding.*` RPC seam and to the per-screen custom elements. This class only tracks
 * `scenario` + `screen` (and the small amount of derived view state needed to render the
 * SECURE phase's approval/reasonCode) and answers `apply(command)` deterministically.
 *
 * Screen order per scenario (mirrors the onboarding prototype's inline state machine,
 * collapsed onto the six `OnboardingScreen` values the RPC contract exposes):
 *   - new:     scan -> invite -> identity -> matchcode -> done
 *   - pair:    scan -> matchcode (auto-observed approval) -> done
 *   - wifi:    scan -> handoff -> identity -> matchcode -> done
 *   - expired: scan -> matchcode (immediately expired) -> [retry-approval] -> done
 *   - lost:    scan -> matchcode -> done (unconfirmed) -> [reconcile-completion] -> done
 *
 * The `scan` screen represents passive network discovery. In the real system discovery
 * completing is a kernel-pushed event, not a user command, so the gate (Task 8) signals it
 * here with the dedicated `advance-discovery` command — the ONLY command that acts while
 * `screen === "scan"` (besides the universal `cancel`/`choose-path`). It advances the
 * screen exactly one step, to the scenario's next screen. Any other command applied while
 * scan is still showing is a genuine no-op: no state change, no notification — it is never
 * reinterpreted as a generic "advance" signal, so a screen's real data is never silently
 * discarded. Applying that same command again — now on the next screen — takes effect
 * normally.
 */
export class OnboardingController {
    #scenario = "new";
    #screen = "scan";
    #approvalState;
    #reasonCode;
    #listeners = new Set();
    get view() {
        return {
            phase: PHASE_BY_SCREEN[this.#screen],
            scenario: this.#scenario,
            screen: this.#screen,
            ...(this.#approvalState === undefined ? {} : { approvalState: this.#approvalState }),
            ...(this.#reasonCode === undefined ? {} : { reasonCode: this.#reasonCode }),
        };
    }
    start(scenario) {
        this.#resetTo(scenario);
        this.#notify();
    }
    apply(command) {
        if (command.kind === "cancel") {
            this.#resetTo(this.#scenario);
            this.#notify();
            return;
        }
        if (command.kind === "choose-path") {
            this.#resetTo(command.scenario);
            this.#notify();
            return;
        }
        if (this.#screen === "scan") {
            if (command.kind === "advance-discovery") {
                this.#advanceFromScan();
                this.#notify();
            }
            return;
        }
        if (this.#transition(command))
            this.#notify();
    }
    onChange(callback) {
        this.#listeners.add(callback);
        return () => this.#listeners.delete(callback);
    }
    #resetTo(scenario) {
        this.#scenario = scenario;
        this.#screen = "scan";
        this.#approvalState = undefined;
        this.#reasonCode = undefined;
    }
    #advanceFromScan() {
        const next = SCREEN_ORDER[this.#scenario][1];
        if (next === undefined)
            return;
        this.#screen = next;
        if (next === "matchcode")
            this.#enterMatchcode();
    }
    #enterMatchcode() {
        if (this.#scenario === "expired") {
            this.#approvalState = "expired";
            this.#reasonCode = REASON_APPROVAL_EXPIRED;
        }
        else {
            this.#approvalState = "waiting";
            this.#reasonCode = undefined;
        }
    }
    #transition(command) {
        switch (this.#screen) {
            case "invite":
                if (command.kind !== "redeem-invitation" && command.kind !== "skip-invitation")
                    return false;
                this.#screen = "identity";
                return true;
            case "identity":
                if (command.kind !== "submit-identity")
                    return false;
                this.#screen = "matchcode";
                this.#enterMatchcode();
                return true;
            case "handoff":
                if (command.kind !== "submit-wifi")
                    return false;
                this.#screen = "identity";
                return true;
            case "matchcode":
                if (command.kind === "confirm-matchcode") {
                    if (this.#approvalState === "expired" || this.#approvalState === "rejected")
                        return false;
                    this.#screen = "done";
                    this.#approvalState = "approved";
                    this.#reasonCode = this.#scenario === "lost" ? REASON_COMPLETION_UNCONFIRMED : undefined;
                    return true;
                }
                if (command.kind === "retry-approval") {
                    if (this.#approvalState !== "expired" && this.#approvalState !== "rejected")
                        return false;
                    this.#approvalState = "waiting";
                    this.#reasonCode = undefined;
                    return true;
                }
                return false;
            case "done":
                if (command.kind !== "reconcile-completion")
                    return false;
                if (this.#reasonCode !== REASON_COMPLETION_UNCONFIRMED)
                    return false;
                this.#reasonCode = undefined;
                return true;
            case "scan":
                return false;
        }
    }
    #notify() {
        const view = this.view;
        for (const listener of this.#listeners)
            listener(view);
    }
}
const REASON_APPROVAL_EXPIRED = "approval-expired";
const REASON_COMPLETION_UNCONFIRMED = "completion-unconfirmed";
const SCREEN_ORDER = {
    expired: ["scan", "matchcode", "done"],
    lost: ["scan", "matchcode", "done"],
    new: ["scan", "invite", "identity", "matchcode", "done"],
    pair: ["scan", "matchcode", "done"],
    wifi: ["scan", "handoff", "identity", "matchcode", "done"],
};
const PHASE_BY_SCREEN = {
    done: "ready",
    handoff: "connect",
    identity: "identify",
    invite: "identify",
    matchcode: "secure",
    scan: "connect",
};
//# sourceMappingURL=onboarding-controller.js.map